// Copyright 2019 Sorama B.V.
import {
    IWorkflowPropertyMetadataSettings,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { IWorkflowModuleReference } from "../Interfaces/IWorkflowModule";

const workflowPropertyMetadataKey = Symbol("WorkflowProperty");

// See https://github.com/Microsoft/TypeScript-Handbook/blob/master/pages/Decorators.md for
// information on how decorators work.
export function workflowProperty(settings: IWorkflowPropertyMetadataSettings): PropertyDecorator {
    return function (target: IWorkflowModuleReference, targetProperty: string) {
        const targetAny = <unknown>target;

        if (typeof targetAny[targetProperty] === "undefined") {
            // Make sure the property is defined, or getAllWorkflowPropertyMetadata will miss this
            // one if it doesn't have a default value in the declaration.
            Object.defineProperty(target, targetProperty, { writable: true, enumerable: true, configurable: true });
        }

        if (!settings.scope) {
            // Set workflow scope as default scope
            settings.scope = WorkflowPropertyScope.workflow;
        }

        if (!settings.objectType) {
            if (settings.scope == WorkflowPropertyScope.workflowModuleSet) {
                throw Error("WorkflowAttribute: object type should be defined when the scope is moduleset.");
            } else {
                settings.objectType = Reflect.getOwnMetadata("design:type", target, targetProperty).name;
            }
        }

        Reflect.defineMetadata(workflowPropertyMetadataKey, settings, target, targetProperty);
    };
}

export function getWorkflowPropertyMetadata(
    target: IWorkflowModuleReference,
    targetProperty: string
): IWorkflowPropertyMetadataSettings {
    return Reflect.getOwnMetadata(workflowPropertyMetadataKey, Object.getPrototypeOf(target), targetProperty);
}

export function getAllWorkflowPropertyMetadata(target: IWorkflowModuleReference): {
    [propertyName: string]: IWorkflowPropertyMetadataSettings;
} {
    // Get all OWN properties of the target object
    const ownProperties = Object.getOwnPropertyNames(target);
    const result: { [propertyName: string]: IWorkflowPropertyMetadataSettings } = {};
    for (const ownProperty of ownProperties) {
        const metadata = getWorkflowPropertyMetadata(target, ownProperty);
        if (!metadata) {
            // Not a WorkflowProperty
            continue;
        }

        result[ownProperty] = metadata;
    }

    // Also iterate over the rest
    for (const property in target) {
        if (Object.prototype.hasOwnProperty.call(result, property)) continue;

        const metadata = getWorkflowPropertyMetadata(target, property);
        if (!metadata) {
            // Not a WorkflowProperty
            continue;
        }

        result[property] = metadata;
    }

    return result;
}
